import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { UserProfile } from '@/lib/types'
import { Avatar } from './Avatar'
import { AvatarStack } from './AvatarStack'
import { cn, profileName } from '@/lib/utils'
import { Check } from 'lucide-react'

interface Props {
  /** The ticket whose subscribers we're editing. */
  ticketId: string
  /** All users who can be picked (project owner + project members). */
  subscribableUserIds: string[]
  /** Current subscribers — checkbox state derived from this. Null /
   *  empty are both valid (= no subscribers yet). The button stays
   *  clickable so the user can ADD the first subscriber. */
  subscriberIds: Set<string> | null
  /** Highlighted as the « primary » in the avatar ring AND prefixed in
   *  the picker — same convention as elsewhere. */
  assigneeId?: string | null
  /** Surfaces a « Vous » badge in the picker rows. */
  currentUserId?: string
  /** Used to display name + avatar for each row + each avatar in the stack. */
  profilesById: Record<string, UserProfile>
  /** Called on every checkbox flip. Same shape as App.toggleSubscriber;
   *  the dispatcher already handles realtime + email side-effects. */
  onToggle: (
    ticketId: string,
    userId: string,
    subscribe: boolean,
  ) => void | Promise<void>
}

/**
 * Click-to-open popover anchored to a card's avatar stack. Lets the user
 * add or remove subscribers without opening the full TicketDetailModal —
 * the request was « cliquer sur les têtes pour ajouter à la volée ».
 *
 * Wraps the existing presentational `AvatarStack` so we don't fork its
 * rendering logic. The whole thing stops pointer/click propagation so
 * the card's own onClick (which would open the detail modal) never fires.
 */
export function SubscriberPicker({
  ticketId,
  subscribableUserIds,
  subscriberIds,
  assigneeId = null,
  currentUserId,
  profilesById,
  onToggle,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  // Coordinates of the popover in viewport space — recomputed on every
  // open + on scroll/resize. We portal the popover to <body> so the
  // ancestor list's overflow:hidden no longer clips it.
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  )

  // Click outside → close. Listening for pointerdown rather than click
  // means we close BEFORE the click bubbles, which keeps the parent
  // card and the DnD pointer sensor un-surprised. The "outside" test
  // includes both the trigger AND the portalled popover.
  useEffect(() => {
    if (!open) return
    function onDown(e: PointerEvent) {
      const target = e.target as Node
      const inTrigger = ref.current?.contains(target) ?? false
      const inPopover = popoverRef.current?.contains(target) ?? false
      if (!inTrigger && !inPopover) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Position the popover above-right of the trigger. Re-compute on
  // open + on scroll/resize so it sticks to the avatar stack even when
  // the list scrolls underneath. We use useLayoutEffect so the first
  // paint already has correct coordinates (no flash at 0,0).
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    function place() {
      const t = triggerRef.current
      if (!t) return
      const r = t.getBoundingClientRect()
      // Default: popover sits ABOVE the trigger, right-aligned to it.
      const POPOVER_WIDTH = 240
      const POPOVER_MAX_HEIGHT = 340 // header + scrollable list approx
      const GAP = 6
      let top = r.top - GAP - POPOVER_MAX_HEIGHT
      let left = r.right - POPOVER_WIDTH
      // If above doesn't fit (e.g. card near top of viewport), flip below.
      if (top < 8) top = r.bottom + GAP
      // Clamp horizontally inside viewport with an 8px gutter.
      if (left < 8) left = 8
      const maxLeft = window.innerWidth - POPOVER_WIDTH - 8
      if (left > maxLeft) left = maxLeft
      setCoords({ top, left })
    }
    place()
    window.addEventListener('scroll', place, true) // capture: catch scroll on any ancestor
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open])

  // Union of (assignee + subscribers) — same logic the card-level avatar
  // stack used inline. Computed here so the trigger reflects reality.
  const stackIds: string[] = []
  if (assigneeId) stackIds.push(assigneeId)
  if (subscriberIds) {
    for (const id of subscriberIds) {
      if (!stackIds.includes(id)) stackIds.push(id)
    }
  }
  if (stackIds.length === 0) return null

  // Sort the picker rows: assignee first, then current viewer, then
  // alphabetical by display name. Stable so the list doesn't reshuffle
  // when subscriptions change.
  const sortedRows = [...subscribableUserIds].sort((a, b) => {
    if (a === assigneeId) return -1
    if (b === assigneeId) return 1
    if (a === currentUserId) return -1
    if (b === currentUserId) return 1
    const nameA = profileName(profilesById[a]).toLowerCase()
    const nameB = profileName(profilesById[b]).toLowerCase()
    return nameA.localeCompare(nameB)
  })

  return (
    <div
      ref={ref}
      className="relative"
      // Stop pointer + click bubbling so the parent card never receives
      // them. Sortable's pointer-sensor cares about pointerdown.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('subscriberPicker.openAria')}
        title={t('subscriberPicker.openTooltip')}
        className="cursor-pointer rounded-full transition-transform hover:scale-105"
      >
        <AvatarStack
          userIds={stackIds}
          profilesById={profilesById}
          assigneeId={assigneeId}
          max={5}
          size="md"
        />
      </button>

      {open && coords && createPortal(
        <div
          ref={popoverRef}
          // Portaled to <body> so the list container's overflow:hidden
          // can no longer clip us. Positioned in viewport space — the
          // `place()` effect above keeps these coords in sync while the
          // list scrolls. z-50 sits below modals (z-[55]+) but above
          // the rest of the board.
          style={{ position: 'fixed', top: coords.top, left: coords.left }}
          className="z-50 w-[240px] overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-[var(--color-border-soft)] px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
            {t('subscriberPicker.header')}
          </div>
          <ul className="max-h-[260px] overflow-y-auto py-1">
            {sortedRows.map((uid) => {
              const profile = profilesById[uid]
              const name =
                profileName(profile) ||
                t('subscriberPicker.unnamed')
              const isSub = subscriberIds?.has(uid) ?? false
              const isAssignee = uid === assigneeId
              const isYou = uid === currentUserId
              return (
                <li key={uid}>
                  <label
                    className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-[12.5px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
                  >
                    {/* Custom checkbox: a small rounded square that fills
                        with the accent + check icon when checked. Plays
                        nicer with the popover aesthetic than the native
                        checkbox. */}
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                        isSub
                          ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white'
                          : 'border-[var(--color-border)] bg-[var(--color-surface)]',
                      )}
                    >
                      {isSub && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                    <input
                      type="checkbox"
                      checked={isSub}
                      onChange={(e) => onToggle(ticketId, uid, e.target.checked)}
                      className="sr-only"
                    />
                    <Avatar
                      profile={profile}
                      userId={uid}
                      noTitle
                      className={cn(
                        'h-5 w-5 shrink-0',
                        isAssignee &&
                          'ring-1 ring-offset-1 ring-offset-[var(--color-surface)] ring-[var(--color-accent)]',
                      )}
                      textClassName="text-[8px]"
                    />
                    <span className="min-w-0 flex-1 truncate">{name}</span>
                    {isAssignee && (
                      <span className="shrink-0 rounded bg-[var(--color-accent-bg)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-accent-text)]">
                        {t('subscriberPicker.assigneeBadge')}
                      </span>
                    )}
                    {!isAssignee && isYou && (
                      <span className="shrink-0 rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                        {t('subscriberPicker.youBadge')}
                      </span>
                    )}
                  </label>
                </li>
              )
            })}
            {sortedRows.length === 0 && (
              <li className="px-3 py-2 text-[11.5px] italic text-[var(--color-text-dim)]">
                {t('subscriberPicker.empty')}
              </li>
            )}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  )
}
